/**
 *
 * @module accountManager
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/account.md
 *
 */

import Check from 'check-types';
import Logging from './../logging/entry';
import Services from './../services/entry';
import Token from './../token/entry';

const TokenManager = Token.TokenManager;
const AccountManagerConfiguration = Services.Configuration.AccountManagerConfiguration;
const AccountClient = Services.Account.AccountClient;
const UserProfile = Services.Account.UserProfile;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    object,
    instanceStrict,
    nonEmptyString,
    nonEmptyObject
} = Check.assert;

/**
 *
 * @class AccountManager
 * @desc Manages the current account and serves as a go between for the public APIs and the AccountClient.
 *
 */
export default class AccountManager {

    /**
     *
     * @param {SDK.Services.Configuration.AccountManagerConfiguration} config
     * @param {SDK.Services.Account.AccountClient} client
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @param {SDK.Token.TokenManager} tokenManager
     * @throws {InvalidArgumentException}
     *
     */
    constructor(config, client, logger = Logging, tokenManager) {

        const errorMessage = {
            ctor: 'AccountManager(config, client, logger, tokenManager)',
            get config() {
                return `${this.ctor} needs SDK.Services.Configuration.AccountManagerConfiguration instance`;
            },
            get client() {
                return `${this.ctor} needs SDK.Services.Account.AccountClient instance`;
            },
            get logger() {
                return `${this.ctor} needs SDK.Logger instance`;
            },
            get tokenManager() {
                return `${this.ctor} needs SDK.Token.TokenManager instance`;
            }
        };

        instanceStrict(config, AccountManagerConfiguration, errorMessage.config, InvalidArgumentException);
        instanceStrict(client, AccountClient, errorMessage.client, InvalidArgumentException);
        instanceStrict(logger, Logging.Logger, errorMessage.logger, InvalidArgumentException);
        instanceStrict(tokenManager, TokenManager, errorMessage.tokenManager, InvalidArgumentException);

        /**
         *
         * @access private
         * @type {SDK.Services.Configuration.AccountManagerConfiguration}
         *
         */
        this.config = config;

        /**
         *
         * @access private
         * @type {SDK.Services.Account.AccountClient}
         *
         */
        this.client = client;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {SDK.Token.TokenManager}
         *
         */
        this.tokenManager = tokenManager;

        this.logger.log(AccountManager.name, 'Created.');

    }

    /**
     *
     * @access private
     * @param {String} identityToken - An identity token representing the validated identity.
     * @param {Object} attributes - A set of attributes to associate with the new account. Note: certain
     * attributes may be required while others are optional.
     * @note {Object} [metadata] - Contains additional info on the account being created like `isTest`
     * to indicate if the account being made is for testing purposes and should have a temporary lifespan
     * @desc Creates a new account and associates with the supplied identity.
     * @returns {Promise<SDK.Services.Account.CreateAccountResult>}
     *
     */
    async createAccount(identityToken, attributes, metadata) {

        const errorMessage = {
            method: 'AccountManager.createAccount(identityToken, attributes, metadata)',
            get identityToken() {
                return `${this.method} identityToken should be a String with length`;
            },
            get attributes() {
                return `${this.method} attributes should be an Object`;
            },
            get metadata() {
                return `${this.method} metadata should be an Object`;
            }
        };

        nonEmptyString(identityToken, errorMessage.identityToken, InvalidArgumentException);
        object(attributes, errorMessage.attributes, InvalidArgumentException);

        if (Check.assigned(metadata)) {
            object(metadata, errorMessage.metadata, InvalidArgumentException);
        }

        const { client, accessToken } = this;

        const options = {
            identityToken,
            attributes,
            accessToken,
            metadata
        };

        return await client.createAccount(options);

    }

    /**
     *
     * @access private
     * @param {String} identityToken - An identity token representing the validated identity.
     * @desc Gets an account grant for the identified user.
     * @returns {Promise<SDK.Services.Account.AccountGrant>}
     *
     */
    async createAccountGrant(identityToken) {

        const errorMessage = {
            method: 'AccountManager.createAccountGrant(identityToken)',
            get identityToken() {
                return `${this.method} identityToken should be a String with length`;
            }
        };

        nonEmptyString(identityToken, errorMessage.identityToken, InvalidArgumentException);

        const { client, accessToken } = this;

        return await client.createAccountGrant(identityToken, accessToken);

    }

    /**
     *
     * @access private
     * @since 3.1.0
     * @desc Gets the current account.
     * @note Attributes are not cached in the SDK, each time the app developer accesses `Account.attributes`
     * it will retrieve them from the service.
     * @returns {Promise<Account>} An account object containing the account ID and a dictionary of account attributes.
     *
     */
    async getAccount() {

        const { client, accessToken } = this;

        return await client.getCurrentAccount(accessToken);

    }

    /**
     *
     * @access private
     * @param {Object} attributes - The attributes to change.
     * @desc Updates the supplied attributes. Ommited attributes are not updated.
     * @returns {Promise<Void>}
     *
     */
    async updateAttributes(attributes) {

        const errorMessage = {
            method: 'AccountManager.updateAttributes(attributes)',
            get attributes() {
                return `${this.method} attributes should be an Object`;
            }
        };

        object(attributes, errorMessage.attributes, InvalidArgumentException);

        const { client, accessToken } = this;

        return await client.updateAttributes(attributes, accessToken);

    }

    /**
     *
     * @access private
     * @since 3.5.0
     * @param {String} profileName - The name of the profile.
     * @param {Object} [attributes=null] - The attributes to use when creating the profile.
     * @param {Object} [metadata=null] - The metadata to use when creating the profile.
     * @desc Creates a user profile.
     * @returns {Promise<SDK.Services.Account.UserProfile>}
     *
     */
    async createUserProfile(profileName, attributes = null, metadata = null) {

        const errorMessage = {
            method: 'AccountManager.createUserProfile(profileName, attributes, metadata)',
            get profileName() {
                return `${this.method} profileName should be a String with length`;
            },
            get attributes() {
                return `${this.method} attributes should be a non empty Object`;
            },
            get metadata() {
                return `${this.method} metadata should be a non empty Object`;
            }
        };

        nonEmptyString(profileName, errorMessage.profileName, InvalidArgumentException);

        if (Check.assigned(attributes)) {
            nonEmptyObject(attributes, errorMessage.attributes, InvalidArgumentException);
        }

        if (Check.assigned(metadata)) {
            nonEmptyObject(metadata, errorMessage.metadata, InvalidArgumentException);
        }

        const { client, accessToken } = this;

        return await client.createUserProfile(profileName, attributes, metadata, accessToken);

    }

    /**
     *
     * @access private
     * @since 3.5.0
     * @param {SDK.Services.Account.UserProfile} profile - The user profile to delete.
     * @desc Deletes the user profile.
     * @returns {Promise<Void>}
     *
     */
    async deleteUserProfile(profile) {

        const errorMessage = {
            method: 'AccountManager.deleteUserProfile(profile)',
            get profile() {
                return `${this.method} profile should be an instance of UserProfile`;
            }
        };

        instanceStrict(profile, UserProfile, errorMessage.profile, InvalidArgumentException);

        const { client, accessToken, tokenManager } = this;
        const deleteUserProfileResult = await client.deleteUserProfile(profile.profileId, accessToken);
        const accountGrant = deleteUserProfileResult.accountGrant;

        if (Check.assigned(accountGrant)) {

            const accessState = tokenManager.getAccessState();
            const accessContextState = accessState.data.contextState;

            await tokenManager.exchangeGrant(accountGrant, accessContextState);

        }

        return Promise.resolve();

    }

    /**
     *
     * @access private
     * @since 3.5.0
     * @param {String} profileId - The ID of the user profile to activate.
     * @desc Returns the user profile specified by the profileId.
     * @returns {Promise<SDK.Services.Account.UserProfile>}
     *
     */
    async getUserProfile(profileId) {

        const errorMessage = {
            method: 'AccountManager.getUserProfile(profileId)',
            get profileId() {
                return `${this.method} profileId should be a String with length`;
            }
        };

        nonEmptyString(profileId, errorMessage.profileId, InvalidArgumentException);

        const { client, accessToken } = this;

        return await client.getUserProfile(profileId, accessToken);

    }

    /**
     *
     * @access private
     * @since 3.5.0
     * @desc Returns the user profiles.
     * @returns {Promise<Array<SDK.Services.Account.UserProfile>>}
     *
     */
    async getUserProfiles() {

        const { client, accessToken } = this;

        return await client.getUserProfiles(accessToken);

    }

    /**
     *
     * @access private
     * @since 3.5.0
     * @param {SDK.Services.Account.UserProfile} profile - The user profile to activate.
     * @desc Sets the active user profile.
     * @returns {Promise<Void>}
     *
     */
    async setActiveUserProfile(profile) {

        const errorMessage = {
            method: 'AccountManager.setActiveUserProfile(profile)',
            get profile() {
                return `${this.method} profile should be an instance of UserProfile`;
            }
        };

        instanceStrict(profile, UserProfile, errorMessage.profile, InvalidArgumentException);

        const { client, accessToken, tokenManager } = this;

        const accountGrant = await client.setActiveUserProfile(profile.profileId, accessToken);
        const accessState = tokenManager.getAccessState();
        const accessContextState = accessState.data.contextState;

        return await tokenManager.exchangeGrant(accountGrant, accessContextState);

    }

    /**
     *
     * @access private
     * @since 3.5.0
     * @desc Returns the active user profile.
     * @returns {Promise<SDK.Services.Account.UserProfile>}
     *
     */
    async getActiveUserProfile() {

        const { client, accessToken } = this;

        return await client.getActiveUserProfile(accessToken);

    }

    /**
     *
     * @access private
     * @since 3.5.0
     * @param {SDK.Services.Account.UserProfile} profile - The user profile to update.
     * @desc Saves all changes made to the user profile.
     * @returns {Promise<SDK.Services.Account.UserProfile>}
     *
     */
    async updateUserProfile(profile) {

        const errorMessage = {
            method: 'AccountManager.updateUserProfile(profile)',
            get profile() {
                return `${this.method} profile should be an instance of UserProfile`;
            }
        };

        instanceStrict(profile, UserProfile, errorMessage.profile, InvalidArgumentException);

        const { client, accessToken } = this;

        const updateUserProfileResult = await client.updateUserProfile(profile, accessToken);

        if (updateUserProfileResult.refreshAccessToken) {
            await this.tokenManager.refreshAccessToken(true);
        }

        return await this.getUserProfile(profile.profileId);

    }

    /* #region private: */

    /**
     *
     * @access private
     * @desc Grabs a fresh AccessToken from the TokenManager instance
     * @returns {SDK.Token.AccessToken}
     *
     */
    get accessToken() {

        return this.tokenManager.getAccessToken();

    }

}



// WEBPACK FOOTER //
// ./src/account/accountManager.js